import React from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import TopBar from '../components/common/TopBar';
import { useParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetTitle } from '../components/hooks/useSetTitle';
import useSetOrg from '../components/hooks/useSetOrg';
import useUserInfo from '../components/hooks/useUserInfo';
import useGetPackager from '../components/hooks/packager/useGetPackager';
import useGlynt from '../components/hooks/useGlynt';
import PackagerView from '../components/packager/PackagerView';

function PackagePage() {
  const { dpid } = useParams<{ dpid: string }>();

  const { loading } = useSetOrg(dpid);
  const userInfo = useUserInfo();
  useSetTitle('Packager');

  if (!dpid) {
    throw new Error("Missing 'dpid' parameter");
  }

  const glynt = useGlynt();
  const client = useQueryClient();
  const { data: packagerData, isLoading, isError, isSuccess } = useGetPackager(dpid);

  const createPackager = useMutation({
    mutationFn: async () => (await glynt).packager.create(dpid, dpid),
    onSuccess: () => client.invalidateQueries({ queryKey: ['packager'] }),
  });

  React.useEffect(() => {
    if (
      isSuccess &&
      packagerData?.count === 0 &&
      !createPackager.isPending &&
      !createPackager.isSuccess &&
      !createPackager.isError
    ) {
      createPackager.mutate();
    }
  }, [isSuccess, packagerData?.count]);

  if (isLoading || userInfo.isLoading || loading || createPackager.isPending) {
    return <LinearProgress />;
  }

  if (isError || !packagerData) {
    return <div>Error loading packager data.</div>;
  }

  if (createPackager.isError) {
    return <div>Failed to create packager: {(createPackager.error as Error).message}</div>;
  }

  if (packagerData.count === 0) {
    return <LinearProgress />;
  }

  return (
    <div id="packager-page">
      <TopBar />
      <PackagerView />
    </div>
  );
}
export default PackagePage;
